/* Auto-apply the best coupon.

   The PDP teaser and the cart's offer drawer already work out the best coupon
   from the backend list (/apps/reviews/offers/coupons). This applies that same
   coupon to the cart for the shopper, so it carries through to checkout
   without a tap:

   - After any cart change, pick the coupon that saves the most at the current
     cart subtotal and set it via POST /cart/update.js { discount }.
   - Swap to a better coupon, or clear ours, as the cart grows or shrinks.
   - Right before the shopper leaves the cart for checkout, re-check once.

   Only coupons from the backend list are managed. A code the shopper (or the
   loyalty flow) put on the cart themselves is never overwritten. Bank offers
   aren't codes, so they're never applied here.

   Config comes from #minaki-auto-apply (snippets/minaki-auto-apply-config.liquid);
   set "enabled": false there to switch the whole thing off. */
(function () {
  var cfgEl = document.getElementById('minaki-auto-apply');
  if (!cfgEl) return;

  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || !cfg.enabled || !cfg.endpoint) return;

  var OWN_HEADER = 'X-Minaki-Auto-Offer';
  var COUPON_CACHE_KEY = 'minaki_auto_coupons';
  var COUPON_TTL_MS = 5 * 60 * 1000;
  var FAIL_TTL_MS = 5 * 60 * 1000;
  var CHECKOUT_GUARD_TIMEOUT_MS = 3000;

  var state = { appliedCode: null, otherCode: null, subtotalCents: 0, ready: false };
  var evaluating = null;
  var rerunQueued = false;
  var memoryCoupons = null;

  /* ---- helpers ---------------------------------------------------------- */

  function ownFetch(url, options) {
    options = options || {};
    options.headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
    options.headers[OWN_HEADER] = '1';
    return fetch(url, options);
  }

  function emit(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) {}
  }

  function setCode(code) {
    return ownFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code }),
    }).then(function (r) { return r.json(); });
  }

  // A code that Shopify refused (missing, expired, minimum not met, ...) is
  // remembered with the subtotal it failed at, so a growing cart gets another
  // try but an unchanged one doesn't hammer /cart/update.js.
  function failedAt(code) {
    try {
      var raw = JSON.parse(sessionStorage.getItem('minaki_auto_fail_' + code));
      if (raw && Date.now() - raw.at < FAIL_TTL_MS) return raw.subtotalCents;
    } catch (e) {}
    return null;
  }
  function writeFail(code, subtotalCents) {
    try {
      sessionStorage.setItem('minaki_auto_fail_' + code, JSON.stringify({ at: Date.now(), subtotalCents: subtotalCents }));
    } catch (e) {}
  }
  function clearFail(code) {
    try { sessionStorage.removeItem('minaki_auto_fail_' + code); } catch (e) {}
  }

  /* ---- coupon list ------------------------------------------------------- */

  // Whole enabled list, no subtotal filter: min_order_value is applied here
  // against the live cart, and the same list tells us which cart codes are
  // "ours" to manage. Cached briefly so cart edits don't re-hit the backend.
  function loadCoupons() {
    if (memoryCoupons && Date.now() - memoryCoupons.at < COUPON_TTL_MS) {
      return Promise.resolve(memoryCoupons.items);
    }
    try {
      var cached = JSON.parse(sessionStorage.getItem(COUPON_CACHE_KEY));
      if (cached && Array.isArray(cached.items) && Date.now() - cached.at < COUPON_TTL_MS) {
        memoryCoupons = cached;
        return Promise.resolve(cached.items);
      }
    } catch (e) {}

    return ownFetch(cfg.endpoint)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.success || !Array.isArray(data.coupons)) return memoryCoupons ? memoryCoupons.items : [];
        var items = data.coupons.filter(function (c) { return c && c.code; });
        memoryCoupons = { items: items, at: Date.now() };
        try { sessionStorage.setItem(COUPON_CACHE_KEY, JSON.stringify(memoryCoupons)); } catch (e) {}
        return items;
      })
      .catch(function () { return memoryCoupons ? memoryCoupons.items : []; });
  }

  // Same math as offer-discovery.js's effectiveDiscountCents, but against the
  // cart subtotal instead of a single product price.
  function savingsCents(coupon, subtotalCents) {
    if (coupon.discount_value == null) return 0;
    if (coupon.discount_type === 'percentage') {
      var amount = subtotalCents * (coupon.discount_value / 100);
      if (coupon.max_discount_value) amount = Math.min(amount, coupon.max_discount_value * 100);
      return Math.round(amount);
    }
    if (coupon.discount_type === 'fixed_amount') return Math.round(coupon.discount_value * 100);
    return 0;
  }

  function candidatesFor(coupons, subtotalCents) {
    return coupons
      .filter(function (c) {
        var min = c.min_order_value == null ? 0 : Math.round(c.min_order_value * 100);
        var save = savingsCents(c, subtotalCents);
        return subtotalCents >= min && save > 0 && save < subtotalCents;
      })
      .sort(function (a, b) { return savingsCents(b, subtotalCents) - savingsCents(a, subtotalCents); });
  }

  /* ---- evaluate + apply -------------------------------------------------- */

  function codeIsApplied(cart, code) {
    return (cart.discount_codes || []).some(function (dc) {
      return String(dc.code).toUpperCase() === String(code).toUpperCase() && dc.applicable;
    });
  }

  function evaluate() {
    if (evaluating) { rerunQueued = true; return evaluating; }

    evaluating = Promise.all([ownFetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.ok ? r.json() : null; }), loadCoupons()])
      .then(function (results) {
        var cart = results[0];
        var coupons = results[1];
        if (!cart) return;

        var managed = {};
        coupons.forEach(function (c) { managed[String(c.code).toUpperCase()] = true; });

        var onCartManaged = null;
        var onCartOther = null;
        (cart.discount_codes || []).forEach(function (dc) {
          if (managed[String(dc.code).toUpperCase()]) onCartManaged = dc.code;
          else if (dc.applicable) onCartOther = dc.code;
        });

        var subtotal = cart.original_total_price || 0;
        state.subtotalCents = subtotal;
        state.otherCode = onCartOther;
        state.appliedCode = onCartManaged && codeIsApplied(cart, onCartManaged) ? onCartManaged : null;

        // A code the shopper chose themselves wins - one code at a time, and
        // we can't know theirs is worse.
        if (onCartOther) return;

        var candidates = cart.item_count ? candidatesFor(coupons, subtotal) : [];
        var best = candidates[0];

        if (best && state.appliedCode && String(state.appliedCode).toUpperCase() === String(best.code).toUpperCase()) return;
        if (!best && !onCartManaged) return;

        if (!best) {
          return setCode('').then(function () {
            state.appliedCode = null;
            emit('minaki:auto-offer', { action: 'removed', code: onCartManaged, subtotal_cents: subtotal });
            emit('cart:refresh');
          });
        }

        // Try the best first, falling back down the list if Shopify refuses one
        // (its real minimum can differ from the backend's display data).
        var tryable = candidates.filter(function (c) {
          var failed = failedAt(c.code);
          return failed === null || subtotal > failed;
        });

        function attempt(i) {
          if (i >= tryable.length) {
            return setCode('').then(function () {
              state.appliedCode = null;
              if (onCartManaged) {
                emit('minaki:auto-offer', { action: 'removed', code: onCartManaged, subtotal_cents: subtotal });
                emit('cart:refresh');
              }
            });
          }
          var coupon = tryable[i];
          return setCode(coupon.code).then(function (updated) {
            if (codeIsApplied(updated, coupon.code)) {
              clearFail(coupon.code);
              state.appliedCode = coupon.code;
              emit('minaki:auto-offer', { action: 'applied', code: coupon.code, subtotal_cents: subtotal });
              emit('cart:refresh');
              return;
            }
            writeFail(coupon.code, subtotal);
            emit('minaki:auto-offer', { action: 'failed', code: coupon.code, subtotal_cents: subtotal });
            return attempt(i + 1);
          });
        }
        return attempt(0);
      })
      .catch(function () {})
      .then(function () {
        state.ready = true;
        evaluating = null;
        emit('minaki:offer-state', state);
        if (rerunQueued) { rerunQueued = false; return evaluate(); }
      });

    return evaluating;
  }

  var debounceTimer = null;
  function scheduleEvaluate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 250);
  }

  /* ---- triggers ---------------------------------------------------------- */

  // Catch every cart mutation regardless of which theme module made it.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function isOwn(init) {
    if (!init || !init.headers) return false;
    var h = init.headers;
    return typeof h.get === 'function' ? !!h.get(OWN_HEADER) : !!h[OWN_HEADER];
  }

  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = nativeFetch.apply(this, arguments);
    if (CART_WRITE.test(url) && !isOwn(init)) {
      promise.then(scheduleEvaluate, function () {});
    }
    return promise;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && CART_WRITE.test(url)) {
      this.addEventListener('loadend', scheduleEvaluate);
    }
    return nativeOpen.apply(this, arguments);
  };

  // offer-discovery's manual Apply and cart-experience announce changes this
  // way. Our own emits fire it too; evaluate() no-ops once settled.
  document.addEventListener('cart:refresh', scheduleEvaluate);
  window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleEvaluate(); });

  // Checkout guard: make sure the right code is on the cart before leaving.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[name="checkout"]');
    if (!btn || btn.dataset.minakiChecked) return;

    e.preventDefault();
    e.stopPropagation();

    var released = false;
    function release() {
      if (released) return;
      released = true;
      btn.dataset.minakiChecked = '1';
      btn.click();
      setTimeout(function () { delete btn.dataset.minakiChecked; }, 1500);
    }
    setTimeout(release, CHECKOUT_GUARD_TIMEOUT_MS); // never trap the shopper
    evaluate().then(release, release);
  }, true);

  window.MinakiAutoOffer = { state: state, evaluate: evaluate };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluate);
  } else {
    evaluate();
  }
})();
